import { usePrintRasnLpnLabelMutation } from 'api/rasn'
import { dispatch, useAppSelector, type RootState } from 'api/redux'
import { switchManualEntryScreen } from 'api/redux/store/application.actions'
import { RasnRlpnDetailsPanel } from 'components/left-detail-panel'
import { MainPanel } from 'components/main-panel'
import { showSnackbar } from 'components/snackbar'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type LabelType,
  type RASN,
  type PrintLabelResponse,
  type CountryOfOrigin,
  type CreateUnitBySkuRequest,
  type RASNFamily
} from 'types'
import { useApiHandler, type ApiHandlerContext } from 'utils/useApiHandler'
import { v4 as uuidv4 } from 'uuid'

import './ScanUnit.css'

import { ActionButtons } from './components/ActionButtons'
import { ManualEntry } from './components/ManualEntry'
import { UpcEntry } from './components/UpcEntry'

interface ScanUnitProps {
  goodsHolder: string | undefined
  goodsHolderTrackingId: string | undefined
  rasnData: RASN
  rasnFamilyData: RASNFamily | undefined
  processedQuantity: number
  createUnitContext: ApiHandlerContext<string>
  createUnitBySkuContext: ApiHandlerContext<CreateUnitBySkuRequest>
  finishRasnContext: () => void
  handlePauseCarton: () => void
  closeGoodsHolderContext: ApiHandlerContext<string>
  showReturnsEndDate: boolean
}

const applicationSelector = (state: RootState) => state.application.isManualEntryScreenOpen

export const ScanUnit = ({
  goodsHolder,
  goodsHolderTrackingId,
  rasnData,
  rasnFamilyData,
  processedQuantity,
  createUnitContext,
  createUnitBySkuContext,
  finishRasnContext,
  handlePauseCarton,
  closeGoodsHolderContext,
  showReturnsEndDate
}: ScanUnitProps) => {
  const { t } = useTranslation()

  const [error, setError] = useState('')
  const [upcBarcode, setUpcBarcode] = useState('')
  const [manualStyleColor, setManualStyleColor] = useState('')
  const [manualSize, setManualSize] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const [printILPNLabel] = usePrintRasnLpnLabelMutation()
  const isManualEntryOpen = useAppSelector(applicationSelector)

  const printLabelContext = useApiHandler({
    apiFunction: async (labelType: LabelType) => await printILPNLabel({ rasnNumber: rasnData.number, labelType }).unwrap(),
    successAction: (response: PrintLabelResponse) => {
      const message = response.status === 'OK' ? t('snackbar.success_printing') : t('snackbar.error_printing')
      const status = response.status === 'OK' ? 'success' : 'error'
      dispatch(showSnackbar(message, status))
      dispatch(() => inputRef.current?.focus())
    },
    failureAction: () => {
      dispatch(showSnackbar(t('snackbar.error_printing'), 'error'))
      dispatch(() => inputRef.current?.focus())
    }
  })

  // Automatic triggering of create unit by SKU when all fields are filled
  const handleConfirmManual = (country: CountryOfOrigin | undefined) => {
    setUnitId()
    createUnitBySkuContext.handleApiCall({ sku: manualStyleColor + '-' + manualSize, countryOfOrigin: country?.countryCode, lpn: null })
      .then()
      .catch(err => {
        if (err.status !== 404) throw err // this case should never be reached, if so it's a bug so throw
        dispatch(showSnackbar(`${t('scan_unit.unknown_sku')} ${manualStyleColor + '-' + manualSize}`, 'error'))
      })
  }

  const handleConfirmUPC = () => {
    if (error === '' && upcBarcode.length > 0 && !createUnitContext.isLoading) {
      setUnitId()
      createUnitContext.handleApiCall(upcBarcode)
        .then()
        .catch(err => {
          if (err.status !== 404) throw err // this case should never be reached, if so it's a bug so throw
          dispatch(showSnackbar(`${t('scan_unit.unknown_upc')} ${upcBarcode}`, 'error'))
        })
      setUpcBarcode('')
    }
  }

  const switchBetweenManualAndUpcScan = () => {
    dispatch(switchManualEntryScreen())
    inputRef.current?.focus()
    setError('')
  }

  const renderManualInput = useMemo(
    () => (
            <ManualEntry styleColor={manualStyleColor}
                      size={manualSize}
                      onStyleColorChange={setManualStyleColor}
                      onSizeChange={setManualSize}
                      styleColorRef={inputRef}
                      confirm={handleConfirmManual}
                      isLoading={createUnitBySkuContext.isLoading}
            />
    ),
    [manualStyleColor, manualSize, createUnitBySkuContext.isLoading, isManualEntryOpen]
  )
  const renderScanInput = useMemo(
    () => (
            <UpcEntry upcBarcode={upcBarcode}
                      upcInputRef={inputRef}
                      error={error}
                      setError={setError}
                      onUpcChange={setUpcBarcode}
                      onConfirm={handleConfirmUPC}
            />
    ),
    [upcBarcode, error, isManualEntryOpen]
  )

  const leftPanelContent =
      <RasnRlpnDetailsPanel goodsHolder={goodsHolder}
                            goodsHolderTrackingId={goodsHolderTrackingId}
                            rasnData={rasnData}
                            rasnFamilyData={rasnFamilyData}
                            processedQuantity={processedQuantity}
                            finishRasnContext={finishRasnContext}
                            closeGoodsHolderContext={closeGoodsHolderContext}
                            handlePauseCarton={handlePauseCarton}
                            showReturnsEndDate={showReturnsEndDate}
      />

  const mainPanelContent =
      <div style={{ display: '-webkit-box' }}>
        <ActionButtons isManualInput={isManualEntryOpen} onSwapScreens={switchBetweenManualAndUpcScan}
                       printLabelContext={printLabelContext}/>
        <div style={{ width: '900px' }} className={'m-auto scanTitle'}>
          {isManualEntryOpen
            ? renderManualInput
            : renderScanInput
          }
        </div>
      </div>

  return <MainPanel leftPanelContent={leftPanelContent} mainPanelContent={mainPanelContent} />
}

const setUnitId = () => {
  // Backend requires a 12 byte hexademical ID, just like RF ID
  localStorage.setItem('unitId', uuidv4().replace(/-/g, '').substring(0, 24))
}
